import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, Users, Eye, TrendingUp, LogIn, LogOut, FileText, Globe, Loader2 } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";

const RANGES = [
  { label: "7 zile", value: 7 },
  { label: "14 zile", value: 14 },
  { label: "30 zile", value: 30 },
];

const prettyPath = (p) => {
  if (!p) return "—";
  if (p === "/") return "Acasă (/)";
  const map = {
    "/magazin": "Magazin", "/cos": "Coș", "/finalizare": "Finalizare comandă",
    "/contact": "Contact", "/autentificare": "Autentificare", "/inregistrare": "Înregistrare",
  };
  return map[p] || p;
};

const fmtDay = (d) => {
  const parts = d.split("-");
  return `${parts[2]}.${parts[1]}`;
};

function MiniTable({ title, icon: Icon, rows, labelKey, valueKey, valueLabel, transform }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-slate-900">
        <Icon className="h-4 w-4 text-primary" /> {title}
      </h3>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="h-8 text-xs">Pagină / Sursă</TableHead>
            <TableHead className="h-8 text-right text-xs">{valueLabel}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={`${r[labelKey]}-${i}`}>
              <TableCell className="py-2 text-sm text-slate-700">{transform ? transform(r[labelKey]) : r[labelKey]}</TableCell>
              <TableCell className="py-2 text-right font-mono text-sm font-semibold text-slate-900">{r[valueKey]}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={2} className="py-6 text-center text-sm text-slate-400">Nu există date încă.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export function AdminAnalytics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/analytics/summary", { params: { days } })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, 20000); // refresh (live) every 20s
    return () => clearInterval(iv);
  }, [load]);

  const chartData = useMemo(
    () => (data?.timeseries || []).map((t) => ({ ...t, label: fmtDay(t.day) })),
    [data]
  );

  if (loading && !data) {
    return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (!data) return null;

  const cards = [
    { label: "Live acum", value: data.live, icon: Activity, accent: "text-emerald-600", live: true },
    { label: "Vizitatori azi", value: data.visitors_today, icon: Users, accent: "text-primary" },
    { label: "Afișări azi", value: data.pageviews_today, icon: Eye, accent: "text-primary" },
    { label: `Vizitatori (${data.days} zile)`, value: data.visitors_range, icon: TrendingUp, accent: "text-primary" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Statistici actualizate automat (fus orar România) · live la fiecare 20s</p>
        <div className="flex gap-1 rounded-full border border-slate-200 bg-white p-1">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                days === r.value ? "bg-primary text-white" : "text-slate-500 hover:text-primary"
              }`}
              data-testid={`analytics-range-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`analytics-card-${c.label}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{c.label}</span>
              <span className="relative">
                {c.live && c.value > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
                  </span>
                )}
                <c.icon className={`h-4 w-4 ${c.accent}`} />
              </span>
            </div>
            <p className={`mt-3 font-heading text-3xl font-bold ${c.live ? "text-emerald-600" : "text-slate-900"}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 font-heading text-sm font-bold text-slate-900">Vizitatori și afișări</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gVis" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0059b3" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#0059b3" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gPv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip
              contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }}
              formatter={(v, n) => [v, n === "visitors" ? "Vizitatori" : "Afișări"]}
            />
            <Area type="monotone" dataKey="visitors" stroke="#0059b3" strokeWidth={2} fill="url(#gVis)" />
            <Area type="monotone" dataKey="pageviews" stroke="#f59e0b" strokeWidth={2} fill="url(#gPv)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <MiniTable title="Cele mai vizitate pagini" icon={FileText} rows={data.top_pages} labelKey="path" valueKey="views" valueLabel="Afișări" transform={prettyPath} />
        <MiniTable title="Surse de trafic" icon={Globe} rows={data.referrers} labelKey="referrer" valueKey="count" valueLabel="Sesiuni" />
        <MiniTable title="Pagini de intrare" icon={LogIn} rows={data.entry_pages} labelKey="path" valueKey="count" valueLabel="Sesiuni" transform={prettyPath} />
        <MiniTable title="Unde părăsesc site-ul (ieșire)" icon={LogOut} rows={data.exit_pages} labelKey="path" valueKey="count" valueLabel="Sesiuni" transform={prettyPath} />
      </div>
    </div>
  );
}
